import {
  getV1CompaniesCompanyIdJobsId,
  getV1CompaniesCompanyIdOrdersOrderIdIdTickets,
  getV1CompaniesCompanyIdOrdersOrderIdJobs,
  getV1CompaniesCompanyIdOrdersOrderIdLoads,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';

import { Job } from '~hooks/useJob/models';
import { Load, NON_CANCELLED_LOAD_STATES } from '~hooks/useLoad/models';
import { Order } from '~hooks/useOrders/models';
import { Ticket } from '~hooks/useTickets/models';
import FilesQueryContext from '~pages/Approvals/Files/FilesQueryContext';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import LoadsTabQueryContext from './LoadsTabQueryContext';

type CrossOrderState = {
  order: Order;
  jobs: Job[];
  loads: Load[];
  tickets: Ticket[];
};

// `undefined` means the drawer is closed; the sentinel means it is open on an order with
// nothing to review, which still needs working prev/next.
export const LOADLESS_ORDER = 'loadless-order';
type OpenLoadId = string | typeof LOADLESS_ORDER | undefined;

type UseCrossOrderNavigationOptions = {
  onNavigate: (job: Job, load?: Load, loadNumber?: number, ticket?: Ticket) => void;
};

function fetchAllLoads(companyId: string, orderId: string) {
  return fetchAllApiPages(
    (query) =>
      getV1CompaniesCompanyIdOrdersOrderIdLoads({
        path: { 'company-id': companyId, 'order-id': orderId },
        query: { 'filter[states]': NON_CANCELLED_LOAD_STATES, ...query },
      }),
    Load.parse,
  );
}

function fetchAllTickets(companyId: string, orderId: string) {
  return fetchAllApiPages(
    (query) =>
      getV1CompaniesCompanyIdOrdersOrderIdIdTickets({
        path: { 'company-id': companyId, 'order-id': orderId },
        query,
      }),
    Ticket.parse,
  );
}

function fetchAllJobs(companyId: string, orderId: string) {
  return fetchAllApiPages(
    (query) =>
      getV1CompaniesCompanyIdOrdersOrderIdJobs({
        path: { 'company-id': companyId, 'order-id': orderId },
        query,
      }),
    Job.parse,
  );
}

export default function useCrossOrderNavigation({
  onNavigate,
}: UseCrossOrderNavigationOptions) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const filesQuery = useContext(FilesQueryContext);
  const loadsTabQuery = useContext(LoadsTabQueryContext);

  const [crossOrderState, setCrossOrderState] = useState<CrossOrderState | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const crossOrderStateRef = useRef(crossOrderState);
  crossOrderStateRef.current = crossOrderState;

  const navigateToOrder = useCallback(
    async (order: Order, direction: 'next' | 'previous') => {
      const [loads, tickets, jobs] = await Promise.all([
        fetchAllLoads(companyId, order.id),
        fetchAllTickets(companyId, order.id),
        fetchAllJobs(companyId, order.id),
      ]);

      if (loads.length === 0) {
        const [firstJob] = jobs;
        if (!firstJob) return;

        setCrossOrderState({ order, jobs, loads, tickets });
        onNavigate(firstJob);

        return;
      }

      const targetLoad = direction === 'next' ? loads[0] : loads[loads.length - 1];
      const targetTicket = tickets.find((t) => t.loadId === targetLoad.id);
      const loadNumber = direction === 'next' ? 1 : loads.length;

      // Fetch the full job for the target load
      const jobResponse = await getV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id: targetLoad.jobId },
      });
      const job = Job.parse(jobResponse.data.data);

      setCrossOrderState({ order, jobs, loads, tickets });
      onNavigate(job, targetLoad, loadNumber, targetTicket);
    },
    [companyId, onNavigate],
  );

  const getCrossOrderNext = useCallback(
    (openLoadId: OpenLoadId, localLoads: Load[]): (() => Promise<void>) | null => {
      if (!openLoadId) return null;

      const currentState = crossOrderStateRef.current;
      const loads = currentState?.loads ?? localLoads;

      if (openLoadId !== LOADLESS_ORDER) {
        const idx = loads.findIndex((l) => l.id === openLoadId);
        if (idx === -1 || idx < loads.length - 1) return null;
      }

      const currentOrderId = currentState?.order.id ?? loadsTabQuery.order.id;
      const orderIndex = filesQuery.data.findIndex((o) => o.id === currentOrderId);

      if (orderIndex < 0) return null;

      const isAtLastLoadedOrder = orderIndex >= filesQuery.data.length - 1;
      // At the boundary of already-loaded pages with no more to fetch — genuinely done.
      if (isAtLastLoadedOrder && !filesQuery.hasMore) return null;

      return async () => {
        setIsFetching(true);
        try {
          // At the boundary of already-loaded pages but more exist server-side —
          // pull the next page in before giving up on finding an adjacent order.
          const orders = isAtLastLoadedOrder
            ? (await filesQuery.fetchMore()).data
            : filesQuery.data;

          const nextOrder = orders[orderIndex + 1];
          if (!nextOrder) return;

          await navigateToOrder(nextOrder, 'next');
        } catch (error) {
          connection.handleRequestError(
            error,
            t('error_messages.jobs.failed_to_navigate_to_adjacent_load'),
          );
        } finally {
          setIsFetching(false);
        }
      };
    },
    [
      filesQuery.data,
      filesQuery.fetchMore,
      filesQuery.hasMore,
      loadsTabQuery.order.id,
      navigateToOrder,
    ],
  );

  const getCrossOrderPrevious = useCallback(
    (openLoadId: OpenLoadId, localLoads: Load[]): (() => Promise<void>) | null => {
      if (!openLoadId) return null;

      const currentState = crossOrderStateRef.current;
      const loads = currentState?.loads ?? localLoads;

      if (openLoadId !== LOADLESS_ORDER) {
        const idx = loads.findIndex((l) => l.id === openLoadId);
        if (idx !== 0) return null;
      }

      const currentOrderId = currentState?.order.id ?? loadsTabQuery.order.id;
      const orders = filesQuery.data;
      const orderIndex = orders.findIndex((o) => o.id === currentOrderId);

      if (orderIndex <= 0) return null;

      const prevOrder = orders[orderIndex - 1];

      return async () => {
        setIsFetching(true);
        try {
          await navigateToOrder(prevOrder, 'previous');
        } catch (error) {
          connection.handleRequestError(
            error,
            t('error_messages.jobs.failed_to_navigate_to_adjacent_load'),
          );
        } finally {
          setIsFetching(false);
        }
      };
    },
    [filesQuery.data, loadsTabQuery.order.id, navigateToOrder],
  );

  const refreshCrossOrderLoads = useCallback(async () => {
    const state = crossOrderStateRef.current;
    if (!state) return;

    const [loads, tickets] = await Promise.all([
      fetchAllLoads(companyId, state.order.id),
      fetchAllTickets(companyId, state.order.id),
    ]);

    setCrossOrderState((prev) => (prev ? { ...prev, loads, tickets } : null));
  }, [companyId]);

  const updateCrossOrderTicket = useCallback((ticket: Ticket, isNew?: boolean) => {
    setCrossOrderState((prev) => {
      if (!prev) return null;

      if (isNew) {
        return { ...prev, tickets: [...prev.tickets, ticket] };
      }

      return {
        ...prev,
        tickets: prev.tickets.map((t) => (t.id === ticket.id ? ticket : t)),
      };
    });
  }, []);

  const resetCrossOrderState = useCallback(() => {
    setCrossOrderState(null);
  }, []);

  const isAtEndOfQueue = useMemo(() => {
    const currentOrderId = crossOrderState?.order.id ?? loadsTabQuery.order.id;
    const orderIndex = filesQuery.data.findIndex((o) => o.id === currentOrderId);

    if (orderIndex < 0) return false;

    return orderIndex >= filesQuery.data.length - 1 && !filesQuery.hasMore;
  }, [
    crossOrderState?.order.id,
    filesQuery.data,
    filesQuery.hasMore,
    loadsTabQuery.order.id,
  ]);

  const isAtStartOfQueue = useMemo(() => {
    const currentOrderId = crossOrderState?.order.id ?? loadsTabQuery.order.id;

    // There is no "fetch previous page", so the first loaded order is the wall.
    return filesQuery.data.findIndex((o) => o.id === currentOrderId) <= 0;
  }, [crossOrderState?.order.id, filesQuery.data, loadsTabQuery.order.id]);

  return {
    crossOrderState,
    isAtEndOfQueue,
    isAtStartOfQueue,
    isFetching,
    getCrossOrderNext,
    getCrossOrderPrevious,
    refreshCrossOrderLoads,
    updateCrossOrderTicket,
    resetCrossOrderState,
  };
}
